import React, { useEffect, useMemo, useState } from "react";
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonIcon,
  IonButtons,
  IonMenuToggle,
  IonToast,
} from "@ionic/react";
import { menu } from "ionicons/icons";
import PictureCard from "./PictureCard";
import MasonryGrid from "../../components/MasonryGrid/MasonryGrid";
import "../../theme/common.css";
import { debug } from "../../common/utils";
import PicturePreview from "./PicturePreview";
import { usePictures } from "./usePictures";
import InfoMessage from "../../components/InfoMessage/InfoMessage";
import appconfig from "../../AppConfig";
import { InfoKids } from "../../components/InfoMessage/InfoKids";
import ToolbarIcons from "../../components/ToolbarIcons/ToolbarIcons";
import { ADDITIONAL_EQUIPMENT_CATEGORY } from "../../common/constants";
import { useParams } from "react-router";
import { useCommon } from "../../common/useCommon";
import { useCategories } from "./useCategories";
import { useCart } from "../Cart/useCart";
import { useCartNotification } from "../Cart/useCartNotification";
import { IPicture } from "../../models";
import { DeliveryText } from "../PackageContent/Content";

const Pictures = () => {
  const { categoryCode } = useParams<{ categoryCode: string }>();

  const { cart, loadCart } = useCart();
  const { cartNotification } = useCartNotification();
  const { allCategories, categories, selectedCategory, selectCategoryByCode } =
    useCategories();
  const { zoom } = useCommon();
  const {
    allPictures,
    availableOnly,
    favorites,
    notifyFavorites,
    getAllPictures,
    loadFavorites,
    hideNotificationFav,
  } = usePictures();

  const [pictures, setPictures] = useState<IPicture[]>([]);

  const getLocalFavorites = (): number[] => {
    const storageString = localStorage.getItem("bpb.pictures.favorites");
    return storageString ? JSON.parse(storageString) : ([] as number[]);
  };
  const getLocalCart = () => {
    const storageString = localStorage.getItem("bpb.cart");
    return storageString ? JSON.parse(storageString) : {};
  };

  useEffect(() => {
    if (allPictures.length === 0) {
      getAllPictures();
    }

    debug("LOAD DEFAULTS");
    loadFavorites(getLocalFavorites());
    loadCart(getLocalCart());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    selectCategoryByCode(categoryCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, categoryCode]);

  const categoryName = React.useMemo(() => {
    return allCategories.find((c) => c.code === categoryCode)?.name || "";
  }, [allCategories, categoryCode]);

  // Filtriraj ako je odabrana akcija (i sortiraj po najvećem popustu) - ako ne:
  // Izbaci kategoriju dodatnu opremu
  // Selektovana kategorija
  // Na stanju
  const computedPictures = useMemo(() => {
    const filtered =
      selectedCategory.code === "akcija"
        ? allPictures
            .filter(
              (p) =>
                p.price !== p.priceNoDiscount &&
                p.priceNoDiscount > 0 &&
                (!availableOnly || p.status),
            )
            .sort((a, b) =>
              (a.priceNoDiscount - a.price) / a.priceNoDiscount <
              (b.priceNoDiscount - b.price) / b.priceNoDiscount
                ? 1
                : -1,
            )
        : allPictures.filter(
            (p) =>
              p.category !== ADDITIONAL_EQUIPMENT_CATEGORY &&
              (p.category === selectedCategory?.id ||
                selectedCategory?.id === 0) &&
              (!availableOnly || p.status),
          );

    // Ako je kategorija "svi" -> randomize
    const pics =
      selectedCategory.code === "svi"
        ? [...filtered].sort(() => Math.random() - 0.5)
        : filtered;

    // Ensure reservations (status false/0) are placed at the end
    return pics.sort((a, b) => Number(b.status) - Number(a.status));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPictures, availableOnly, selectedCategory, cart, favorites]); // cart & favorites are here to refresh randomization on adding to cart or favories

  useEffect(() => {
    setPictures(computedPictures);
  }, [computedPictures]);

  return (
    <IonPage id="picatures">
      <IonHeader>
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonMenuToggle>
              <IonIcon icon={menu} style={{ fontSize: 28 }} />
            </IonMenuToggle>
          </IonButtons>
          <IonTitle>{categoryName}</IonTitle>
          <ToolbarIcons />
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 5,
            padding: 5,
          }}
        >
          {appconfig.showDeliveryInfoBanner && <DeliveryText centered />}
          {selectedCategory?.code === "najmladji" && (
            <InfoMessage content={<InfoKids />} />
          )}
        </div>
        {/* <div>{JSON.stringify({ categoryCode })}</div>
        <div>{JSON.stringify({ picturesFiltered })}</div>
        <div>{JSON.stringify({ selectedCategory })}</div> */}
        <PicturePreview />

        <MasonryGrid columns={zoom} mobileColumns={Math.min(zoom, 2)}>
          {pictures.map((pic) => (
            <PictureCard key={pic.id} pic={pic} />
          ))}
        </MasonryGrid>
      </IonContent>

      <IonToast
        isOpen={notifyFavorites}
        onDidDismiss={hideNotificationFav}
        message={"Omiljene slike su ažurirane."}
        position="bottom"
        duration={5000}
        buttons={[
          {
            text: "OK",
            role: "cancel",
            handler: () => {
              debug("Cancel clicked");
            },
          },
        ]}
      />
      {cartNotification}
    </IonPage>
  );
};

export default Pictures;
